import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children, queryClient }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in on mount
    const checkAuth = async () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          const response = await authAPI.getProfile();
          setUser(response.data);
        } catch (error) {
          // Clear tokens if they're invalid
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
        }
      }
      setLoading(false);
    };
    
    checkAuth();
  }, []); // Only run on mount

  const login = async (email, password) => {
    try {
      const response = await authAPI.login({ email, password });
      const { access, refresh, user: userData } = response.data;
      
      localStorage.setItem('access_token', access);
      localStorage.setItem('refresh_token', refresh); // Save refresh token
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || 'Login failed'  // Return the full error response data
      };
    }
  };

  const signup = async (name, email, password, confirmPassword) => {
    try {
      await authAPI.signup({
        name,
        email,
        password,
        password_confirm: confirmPassword
      });
      
      // Don't automatically log in after signup
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || 'Signup failed'  // Return the full error response data
      };
    }
  };

  const logout = async () => {
    try {
      const refresh = localStorage.getItem('refresh_token');
      if (user && refresh) { // Only call logout API if user is logged in and we have refresh token
        await authAPI.logout({ refresh }); // Send refresh token in request body
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setUser(null);
      if (queryClient) {
        queryClient.clear();
      }
      navigate('/login');
    }
  };

  const forgotPassword = async (email) => {
    try {
      await authAPI.requestPasswordReset(email);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to process request'
      };
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      await authAPI.resetPassword({
        token,
        new_password: newPassword,
        new_password_confirm: newPassword
      });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.response?.data?.error || 'Password reset failed'
      };
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await authAPI.updateProfile(profileData);
      setUser(response.data);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Profile update failed'
      };
    }
  };

  const updatePassword = async (currentPassword, newPassword, newPasswordConfirm) => {
    try {
      await authAPI.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirm: newPasswordConfirm
      });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || 'Password change failed'
      };
    }
  };

  const value = {
    user,
    loading,
    login,
    signup,
    logout,
    forgotPassword,
    resetPassword,
    updateProfile,
    updatePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext); 